/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { Typography2Variant } from '@coursera/cds-core';
import { Grid, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import CardPlaceholder from 'bundles/shared-articles/components/ArticleCardPlaceholder';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import ArticleSlider from 'bundles/unified-description-page-common/components/sliders/ArticleSlider';
import {
  ARTICLES_RECS_TRACKING_NAME,
  NUMBER_OF_ARTICLES_RECS_TO_SHOW,
} from 'bundles/unified-description-page-common/constants';
import type { ArticleHit } from 'bundles/unified-description-page-common/contexts/consumer/ArticlesRecommendationsContext';
import {
  useMobileOnlyStreamlinedXDP,
  useStreamlinedXDP,
} from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';
import OnsiteRecommendationCard from 'bundles/unified-seo-content-common/components/shared/OnsiteRecommendationCard';

type InputProps = {
  recommendations?: ArticleHit[];
  isRecommendationsLoading: boolean;
  title: string;
  trackingData: Record<string, unknown>;
  titleTypographyVariant?: Typography2Variant;
};

export type Props = InputProps;

const styles = {
  title: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  cardsContainer: css`
    ${breakpoints.down('xs')} {
      div:nth-of-type(n + 3) {
        display: none;
      }
    }
  `,
};

const ArticlesRecommendationsModuleNotConfigurable = ({
  title,
  recommendations,
  isRecommendationsLoading,
  trackingData,
  titleTypographyVariant,
}: Props) => {
  const isMobileOnlyStreamlinedXDP = useMobileOnlyStreamlinedXDP();
  const isStreamlinedXDP = useStreamlinedXDP();

  if (!isRecommendationsLoading && (!recommendations || recommendations.length <= 2)) {
    return null;
  }

  const displayedRecommendations = isMobileOnlyStreamlinedXDP ? (
    <ArticleSlider
      articleItems={
        recommendations?.map((hit) => {
          return (
            <OnsiteRecommendationCard
              key={hit.objectUrl}
              slug={hit.objectUrl.replace('/articles/', '')}
              trackingName={`${ARTICLES_RECS_TRACKING_NAME}_card`}
              title={hit.name}
              subtitle={hit.introduction}
              metadata={{ publishedDate: hit.lastPublishedAt }}
              alwaysShowSubtitle={isMobileOnlyStreamlinedXDP}
            />
          );
        }) || []
      }
    />
  ) : (
    recommendations?.map((hit) => {
      return (
        <Grid item xs key={hit.objectUrl}>
          <OnsiteRecommendationCard
            slug={hit.objectUrl.replace('/articles/', '')}
            trackingName={`${ARTICLES_RECS_TRACKING_NAME}_card`}
            title={hit.name}
            metadata={{ publishedDate: hit.lastPublishedAt }}
            subtitle={isStreamlinedXDP ? hit.introduction : undefined}
            alwaysShowSubtitle={isStreamlinedXDP}
          />
        </Grid>
      );
    }) || null
  );

  return (
    <TrackedDiv
      trackingName={ARTICLES_RECS_TRACKING_NAME}
      data={trackingData}
      withVisibilityTracking
      trackClicks={false}
    >
      <Section>
        <PageGridContainer>
          <Grid css={isMobileOnlyStreamlinedXDP ? '' : styles.cardsContainer} item container xs={12} spacing={16}>
            <Grid item xs={12}>
              <Typography2 component="h2" css={styles.title} variant={titleTypographyVariant ?? 'subtitleLarge'}>
                {title}
              </Typography2>
            </Grid>

            {isRecommendationsLoading
              ? new Array(NUMBER_OF_ARTICLES_RECS_TO_SHOW).fill(1).map(() => (
                  <Grid item xs>
                    <CardPlaceholder showImage={false} showSubtitle={false} />
                  </Grid>
                ))
              : displayedRecommendations}
          </Grid>
        </PageGridContainer>
      </Section>
    </TrackedDiv>
  );
};

export default ArticlesRecommendationsModuleNotConfigurable;
